import React, { useEffect, useState, useRef, useCallback, useLayoutEffect } from 'react';
import { Message, Conversation } from '../types';
import { authenticatedFetch } from '../libraries/auth';
import { useAppDispatch, useAppState, markConversationSeen, updateConversationLastMessage } from '../libraries/AppContext';
import { getFriendlyErrorMessage } from '../libraries/friendlyErrors';
import { useSocket } from '../libraries/SocketContext';
import LoadingSpinner from './LoadingSpinner';
import Alert from './ErrorDisplay';
import FloatingMessageBox from './FloatingMessageBox';
import Modal from './Modal';

interface ChatWindowProps {
    conversationId: number;
}

const API_URL = import.meta.env.VITE_API_URL || '';
const PAGE_SIZE = 20;

const ChatWindow: React.FC<ChatWindowProps> = ({ conversationId }) => {
    const dispatch = useAppDispatch();
    const { eventData } = useAppState();
    const { socket, setCurrentConversationId } = useSocket();
    const [messages, setMessages] = useState<Message[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newMessage, setNewMessage] = useState('');
    const [nextCursor, setNextCursor] = useState<number | null>(null);
    const [conversation, setConversation] = useState<Conversation | null>(null);
    const [reportingMessageId, setReportingMessageId] = useState<number | null>(null);
    const [reportFeedback, setReportFeedback] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
    const [reportModalMessageId, setReportModalMessageId] = useState<number | null>(null);
    const [reportReason, setReportReason] = useState('');
    const [reportReasonError, setReportReasonError] = useState<string | null>(null);

    const shouldScrollToBottomRef = useRef(true);
    const forceScrollOnNextMessageRef = useRef(false);
    const hasFetchedRef = useRef(false);

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const lastMessageCountRef = useRef(0);
    const [composerElement, setComposerElement] = useState<HTMLDivElement | null>(null);
    const [composerHeight, setComposerHeight] = useState(88);
    const hasInitialScrollDoneRef = useRef(false);

    const scrollToBottom = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
        container.scrollTop = container.scrollHeight;
    }, []);

    const scheduleScrollToBottom = useCallback(() => {
        requestAnimationFrame(() => scrollToBottom());
    }, [scrollToBottom]);

    const isNearBottom = (container: HTMLDivElement | null, threshold: number = 80) => {
        if (!container) return true;
        return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    };

    useLayoutEffect(() => {
        if (!composerElement) return;

        const updateComposerHeight = () => {
            setComposerHeight(composerElement.offsetHeight || 88);
        };

        updateComposerHeight();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(() => updateComposerHeight());
        observer.observe(composerElement);
        return () => observer.disconnect();
    }, [composerElement]);

    // Set current conversation ID for the global socket to know which chat is open
    useEffect(() => {
        setCurrentConversationId(conversationId);

        return () => {
            setCurrentConversationId(null);
        };
    }, [conversationId, setCurrentConversationId]);

    // Listen for new messages from the global socket
    useEffect(() => {
        if (!socket) return;

        const handleNewMessage = (message: Message) => {
            if (message.conversationId === conversationId) {
                if (forceScrollOnNextMessageRef.current) {
                    shouldScrollToBottomRef.current = true;
                    forceScrollOnNextMessageRef.current = false;
                } else {
                    shouldScrollToBottomRef.current = isNearBottom(messagesContainerRef.current);
                }
                setMessages((prev) => [...prev, message]);
            }
        };

        socket.on('new_message', handleNewMessage);

        return () => {
            socket.off('new_message', handleNewMessage);
        };
    }, [socket, conversationId]);

    // Track messages change and scroll if needed
    useEffect(() => {
        console.log("Messages updated, count:", messages.length, "lastMessageCountRef", lastMessageCountRef.current, "shouldScrollToBottom:", shouldScrollToBottomRef.current);
        const appendedNewMessage = messages.length > lastMessageCountRef.current;
        lastMessageCountRef.current = messages.length;

        if (shouldScrollToBottomRef.current && appendedNewMessage) {
            scheduleScrollToBottom();
        }
    }, [messages, scheduleScrollToBottom]);

    // Fetch initial messages and conversation details - only once when component mounts
    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        hasInitialScrollDoneRef.current = false;
        lastMessageCountRef.current = 0;
        shouldScrollToBottomRef.current = true;

        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Fetch messages
                const msgResponse = await authenticatedFetch(`${API_URL}/api/chat/${conversationId}/messages?limit=${PAGE_SIZE}`);
                if (!msgResponse.ok) throw new Error('Failed to fetch messages');
                const msgData = await msgResponse.json();

                const fetchedMessages: Message[] = msgData.messages.reverse();
                setMessages(fetchedMessages);
                setNextCursor(msgData.nextCursor);

                // Update the last message in global state if we have messages
                if (fetchedMessages.length > 0) {
                    const lastMessage = fetchedMessages[fetchedMessages.length - 1];
                    updateConversationLastMessage(dispatch, conversationId, lastMessage);
                }

                // Mark conversation as seen on the server
                await authenticatedFetch(`${API_URL}/api/chat/${conversationId}/seen`, {
                    method: 'POST'
                });

                // Update local state to clear unseen count
                markConversationSeen(dispatch, conversationId);

            } catch (err) {
                setError('Could not load chat');
                console.error(err);
                hasFetchedRef.current = false; // Allow retry on error
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [conversationId, dispatch]);

    // Update conversation from global state when it changes
    useEffect(() => {
        const foundConv = eventData?.chat_conversations?.find(c => c.id === conversationId);
        if (foundConv) {
            setConversation(foundConv);
        }
    }, [eventData?.chat_conversations, conversationId]);

    // Retry function for error state
    const handleRetry = useCallback(() => {
        hasFetchedRef.current = false;
        setError(null);
        setLoading(true);

        const fetchData = async () => {
            try {
                const msgResponse = await authenticatedFetch(`${API_URL}/api/chat/${conversationId}/messages?limit=${PAGE_SIZE}`);
                if (!msgResponse.ok) throw new Error('Failed to fetch messages');
                const msgData = await msgResponse.json();

                const fetchedMessages: Message[] = msgData.messages.reverse();
                setMessages(fetchedMessages);
                setNextCursor(msgData.nextCursor);

                // Update the last message in global state if we have messages
                if (fetchedMessages.length > 0) {
                    const lastMessage = fetchedMessages[fetchedMessages.length - 1];
                    updateConversationLastMessage(dispatch, conversationId, lastMessage);
                }

                await authenticatedFetch(`${API_URL}/api/chat/${conversationId}/seen`, {
                    method: 'POST'
                });
                markConversationSeen(dispatch, conversationId);
                hasFetchedRef.current = true;
            } catch (err) {
                setError('Could not load chat');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [conversationId, dispatch]);

    // Scroll to bottom on initial open after messages and layout are ready
    useEffect(() => {
        if (!loading && !error && messages.length > 0 && !hasInitialScrollDoneRef.current) {
            hasInitialScrollDoneRef.current = true;
            shouldScrollToBottomRef.current = true;
            scheduleScrollToBottom();
        }
    }, [loading, error, messages.length, scheduleScrollToBottom]);

    useEffect(() => {
        if (!loading && !error && shouldScrollToBottomRef.current) {
            scheduleScrollToBottom();
        }
    }, [composerHeight, loading, error, scheduleScrollToBottom]);

    const loadMoreMessages = async () => {
        if (!nextCursor || loadingMore) return;

        // Save current scroll height to maintain position
        const container = messagesContainerRef.current;
        const oldScrollHeight = container?.scrollHeight || 0;
        const oldScrollTop = container?.scrollTop || 0;

        setLoadingMore(true);
        try {
            const response = await authenticatedFetch(`${API_URL}/api/chat/${conversationId}/messages?limit=${PAGE_SIZE}&cursor=${nextCursor}`);
            if (!response.ok) throw new Error('Failed to fetch more messages');
            const data = await response.json();

            const olderMessages = data.messages.reverse();
            setMessages(prev => [...olderMessages, ...prev]);
            setNextCursor(data.nextCursor);

            // Restore scroll position roughly
            requestAnimationFrame(() => {
                if (container) {
                    const newScrollHeight = container.scrollHeight;
                    container.scrollTop = newScrollHeight - oldScrollHeight + oldScrollTop;
                }
            });

        } catch (err) {
            console.error("Error loading more messages", err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollTop === 0 && nextCursor) {
            loadMoreMessages();
        }

        const isAtBottom = scrollHeight - scrollTop - clientHeight < 80;
        shouldScrollToBottomRef.current = isAtBottom;
    };

    const handleSendMessage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim() || !socket) return;

        // Force scroll on my message
        shouldScrollToBottomRef.current = true;
        forceScrollOnNextMessageRef.current = true;

        // Optimistic update could be done here, but socket is fast enough usually.
        socket.emit('send_message', {
            conversationId,
            content: newMessage
        });
        setNewMessage('');
    };

    const openReportModal = (messageId: number) => {
        setReportModalMessageId(messageId);
        setReportReason('');
        setReportReasonError(null);
    };

    const closeReportModal = () => {
        if (reportingMessageId !== null) return;
        setReportModalMessageId(null);
        setReportReason('');
        setReportReasonError(null);
    };

    const handleSubmitReport = async () => {
        if (!reportModalMessageId) return;
        if (!reportReason.trim()) {
            setReportReasonError('Bitte geben Sie einen Grund für die Meldung ein.');
            return;
        }

        try {
            setReportingMessageId(reportModalMessageId);
            setReportReasonError(null);
            setReportFeedback(null);

            const response = await authenticatedFetch(`${API_URL}/api/chat/messages/${reportModalMessageId}/report`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({reason: reportReason.trim()}),
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                const apiError = String(data?.error || 'Nachricht konnte nicht gemeldet werden');
                if (apiError.toLowerCase().includes('own message')) {
                    throw new Error('Eigene Nachrichten können nicht gemeldet werden.');
                }
                throw new Error(apiError);
            }

            closeReportModal();
            setReportFeedback({type: 'success', message: 'Vielen Dank, unsere Moderatoren wurden benachrichtigt.'});
        } catch (err) {
            console.error('Error reporting message:', err);
            setReportFeedback({
                type: 'error',
                message: getFriendlyErrorMessage(err, {
                    genericMessage: 'Die Meldung konnte gerade nicht gesendet werden. Bitte versuchen Sie es erneut.',
                    participantMessage: 'Die Meldung konnte gerade nicht gesendet werden. Bitte versuchen Sie es später erneut.',
                }),
            });
        } finally {
            setReportingMessageId(null);
        }
    };

    if (loading) return <LoadingSpinner />;
    if (error) return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 p-4">
            <Alert title="Error" message={error} type="error" onRetry={handleRetry} />
        </div>
    );

    const otherUser = conversation?.otherParticipant;

    return (
        <div className="relative flex flex-col h-full bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 flex-1 min-h-0">
            {reportFeedback && (
                <FloatingMessageBox
                    title={reportFeedback.type === 'success' ? 'Erfolg' : 'Fehler'}
                    type={reportFeedback.type}
                    message={reportFeedback.message}
                    autoHideMs={3000}
                    onClose={() => setReportFeedback(null)}
                />
            )}
            <Modal
                isOpen={reportModalMessageId !== null}
                onClose={closeReportModal}
                title="Nachricht melden"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-600">
                        Bitte beschreiben Sie kurz, warum Sie diese Nachricht melden möchten.
                    </p>
                    <textarea
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                        rows={4}
                        placeholder="Grund der Meldung..."
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-pink-500 focus:ring focus:ring-pink-200 focus:ring-opacity-50"
                        disabled={reportingMessageId !== null}
                    />
                    {reportReasonError && (
                        <p className="text-sm text-red-600">{reportReasonError}</p>
                    )}
                    <div className="flex items-center justify-end gap-2">
                        <button
                            type="button"
                            onClick={closeReportModal}
                            disabled={reportingMessageId !== null}
                            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            onClick={handleSubmitReport}
                            disabled={reportingMessageId !== null}
                            className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-4 py-2 text-sm font-medium text-white hover:bg-pink-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {reportingMessageId !== null && <LoadingSpinner size="sm" />}
                            {reportingMessageId !== null ? 'Wird gesendet...' : 'Meldung senden'}
                        </button>
                    </div>
                </div>
            </Modal>
            {/* Messages Area */}
            <div
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto scroll-smooth px-4 py-0 bg-white min-h-0"
                onScroll={handleScroll}
                style={{ marginBottom: `${composerHeight}px` }}
            >
                {loadingMore && <div className="text-center py-2"><LoadingSpinner /></div>}

                <div className="space-y-4 py-4">
                    {messages.map((msg) => {
                    // Check if msg sender is current user.
                    // Current user ID is accessible via auth context. 
                    // BUT, message senderId is ParticipantID, not UserID.
                    // We need to know current user's participantId. 
                    // We can infer it: if keys match known "me" logic or simply check alignment.
                    // Better: We stored `senderId` in message.
                    // We need `myParticipantId`. Pass it to component or derive. 
                    // For now, let's assume `currentUser.participantId` is available in `User` type or loaded. 
                    // Wait, `User` type in AuthContext might not have `participantId`.
                    // Let's check `User` type in `libraries/auth.ts`.

                    // Assume we align based on sender.id vs derived ID. 
                    // Actually, message.senderId is what we have.
                    // If we don't know myParticipantId, we can't distinguish perfectly unless we compare with `msg.senderId`.
                    // However, in `ChatWindow` we don't have `myParticipantId`.
                    // Let's pass it in props or fetch it.
                    // Or assume align right if `msg.sender.id !== otherUser.id`? No, that assumes only 2 people.
                    // Yes, 1-on-1 chat. So if sender is NOT otherUser, it's ME.

                    const isMe = otherUser ? msg.senderId !== otherUser.id : false;

                    return (
                        <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[75%] flex flex-col ${isMe ? 'items-end' : 'items-start'}`}>
                                <div className={`rounded-2xl px-4 py-2 shadow-sm ${isMe
                                    ? 'bg-pink-600 text-white rounded-tr-none'
                                    : 'bg-gray-100 text-gray-800 rounded-tl-none'
                                    }`}>
                                    <p className="text-sm md:text-base wrap-break-word">{msg.content}</p>
                                    <span className={`text-[10px] block mt-1 ${isMe ? 'text-pink-100' : 'text-gray-500'}`}>
                                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                {!isMe && (
                                    <button
                                        type="button"
                                        onClick={() => openReportModal(msg.id)}
                                        disabled={reportingMessageId === msg.id}
                                        className="mt-1 text-xs underline transition-colors text-gray-500 hover:text-gray-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {reportingMessageId === msg.id ? 'Wird gemeldet...' : 'Nachricht melden'}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                    })}
                </div>
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="fixed left-0 right-0 bottom-0 z-40 pointer-events-none">
                <div className="container mx-auto px-4 max-w-sm md:max-w-2xl lg:max-w-4xl">
                    <div
                        ref={setComposerElement}
                        className="pointer-events-auto p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] bg-gray-50 border-t border-gray-200 rounded-t-lg shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
                    >
                        <form onSubmit={handleSendMessage} className="flex gap-2">
                            <input
                                type="text"
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Nachricht schreiben..."
                                className="flex-1 rounded-full border-gray-300 focus:border-pink-500 focus:ring focus:ring-pink-200 focus:ring-opacity-50 px-4 py-2"
                            />
                            <button
                                type="submit"
                                disabled={!newMessage.trim()}
                                className="bg-pink-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                            >
                                <svg className="w-5 h-5 translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatWindow;
